import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { $apiV1 } from "../../../../config/api";
import { findMaxSecondMarker } from "../../../../helpers";
import {
  buildMarkerColorMap,
  buildParameterColorMap,
} from "../../../../helpers/eventMarkerColors";
import {
  EVENTS_RESULT_LIMIT,
  fetchEventsResultCount,
} from "../eventsResultLimit";

const DEFAULT_TIMELINE_RANGE = { min: 0, max: 90 };
const SET_PIECES_GROUP_LEXIC_ID = 730;

const toNumericId = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const uniqueNumericIds = (values = []) => {
  return values.reduce((acc, value) => {
    const numericValue = toNumericId(value);
    if (numericValue === null || acc.includes(numericValue)) {
      return acc;
    }

    acc.push(numericValue);
    return acc;
  }, []);
};

const isTeamSourcedSetPieceAction = (action) => {
  const groupLexicId = toNumericId(
    action?.group_lexic_id ?? action?.groupLexicId
  );

  return (
    Boolean(action?.team_parameter_id ?? action?.teamParameterId) ||
    groupLexicId === SET_PIECES_GROUP_LEXIC_ID
  );
};

const normalizeName = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const getEventCreatorId = (event) => {
  return toNumericId(
    event?.creator?.id ??
      event?.creator?.player_id ??
      event?.creator?.playerId ??
      event?.creator?.player?.id ??
      event?.player_id ??
      event?.playerId ??
      event?.player?.id
  );
};

const getEventCreatorName = (event) => {
  return normalizeName(
    event?.creator?.display_name ??
      event?.creator?.name ??
      event?.player?.display_name ??
      event?.player?.name
  );
};

const getMarkerKey = (marker) => {
  if (marker && typeof marker === "object") {
    return String(marker.id ?? marker.marker_id ?? marker.markerId ?? "");
  }

  return String(marker ?? "");
};

const filterSearchResultForPlayer = ({ data, playerId, playerName }) => {
  const normalizedPlayerId = toNumericId(playerId);
  const normalizedPlayerName = normalizeName(playerName);

  if (normalizedPlayerId === null && !normalizedPlayerName) {
    return data;
  }

  const filteredEvents = (data?.events || []).filter((event) => {
    const creatorId = getEventCreatorId(event);
    if (normalizedPlayerId !== null && creatorId !== null) {
      return creatorId === normalizedPlayerId;
    }

    const creatorName = getEventCreatorName(event);
    return Boolean(normalizedPlayerName && creatorName === normalizedPlayerName);
  });

  const filteredEventIds = new Set(
    filteredEvents.map((event) => String(event?.id ?? "")).filter(Boolean)
  );
  const filteredAllMarkers = (data?.allMarkers || []).filter((marker) =>
    filteredEventIds.has(getMarkerKey(marker))
  );

  return {
    ...data,
    events: filteredEvents,
    allMarkers: filteredAllMarkers,
  };
};

export const usePlayerDualSideEventsData = ({
  id,
  teamId,
  playerId = id,
  playerName,
  matchesList,
  checkedIds,
  checkedParamIds = checkedIds,
  checkedAction,
  mergeOffset,
  buildSearchPayload,
  onError,
  searchDeps = [],
  keepMaxTimeline = false,
  initialTimelineRange = DEFAULT_TIMELINE_RANGE,
}) => {
  const initialTimelineMin =
    initialTimelineRange?.min ?? DEFAULT_TIMELINE_RANGE.min;
  const initialTimelineMax =
    initialTimelineRange?.max ?? DEFAULT_TIMELINE_RANGE.max;

  const [markers, setMarkers] = useState([]);
  const [opponentsMarkers, setOpponentsMarkers] = useState([]);
  const [response, setResponse] = useState(null);
  const [responseOpponents, setResponseOpponents] = useState(null);

  const [ownMarkerColorMap, setOwnMarkerColorMap] = useState({});
  const [opponentMarkerColorMap, setOpponentMarkerColorMap] = useState({});

  const [allOwnMarkers, setAllOwnMarkers] = useState([]);
  const [allOpponentsMarkers, setAllOpponentsMarkers] = useState([]);
  const [allMergeMarkers, setAllMergeMarkers] = useState([]);

  const [loadingMarkers, setLoadingMarkers] = useState(false);
  // Per-side result-count guard: null = within limit, otherwise the (over-limit) count to display.
  const [limitExceededBySide, setLimitExceededBySide] = useState({
    own: null,
    opponent: null,
  });
  const [timeLineRange, setTimeLineRange] = useState({
    min: initialTimelineMin,
    max: initialTimelineMax,
  });

  const pendingRequestsRef = useRef(0);
  const timelineMaxRef = useRef(0);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const selectedParameterIds = useMemo(() => {
    const selectedActions = Array.isArray(checkedAction) ? checkedAction : [];

    if (selectedActions.length === 0) {
      return {
        playerParamIds: uniqueNumericIds(checkedParamIds),
        teamParamIds: [],
        hasTeamSetPieceSelection: false,
      };
    }

    const playerParamIds = [];
    const teamParamIds = [];

    selectedActions.forEach((action) => {
      if (isTeamSourcedSetPieceAction(action)) {
        teamParamIds.push(action?.team_parameter_id ?? action?.teamParameterId);
        return;
      }

      playerParamIds.push(action?.parameter_id ?? action?.parameterId ?? action?.id);
    });

    return {
      playerParamIds: uniqueNumericIds(playerParamIds),
      teamParamIds: uniqueNumericIds(teamParamIds),
      hasTeamSetPieceSelection: teamParamIds.length > 0,
    };
  }, [checkedAction, checkedParamIds]);

  const updateLoading = useCallback((delta) => {
    pendingRequestsRef.current += delta;
    setLoadingMarkers(pendingRequestsRef.current > 0);
  }, []);

  const resetData = useCallback(() => {
    setMarkers([]);
    setOpponentsMarkers([]);
    setResponse(null);
    setResponseOpponents(null);
    setOwnMarkerColorMap({});
    setOpponentMarkerColorMap({});
    setAllOwnMarkers([]);
    setAllOpponentsMarkers([]);
    setAllMergeMarkers([]);
    setLimitExceededBySide({ own: null, opponent: null });
    setTimeLineRange({
      min: initialTimelineMin,
      max: initialTimelineMax,
    });
    timelineMaxRef.current = 0;
  }, [initialTimelineMax, initialTimelineMin]);

  const clearSideResults = useCallback((needOpponentMarkers) => {
    if (needOpponentMarkers) {
      setResponseOpponents(null);
      setOpponentMarkerColorMap({});
      setAllOpponentsMarkers([]);
      return;
    }

    setResponse(null);
    setOwnMarkerColorMap({});
    setAllOwnMarkers([]);
  }, []);

  const fetchPlayerStatsSide = useCallback(
    async ({ needOpponentMarkers, setMarkersFunc }) => {
      updateLoading(1);

      try {
        const statsResponses = [];

        if (selectedParameterIds.playerParamIds.length > 0) {
          const playerStatsResponse = await $apiV1.get(`/stats/player/${id}`, {
            params: {
              matches: matchesList.join(","),
              params: selectedParameterIds.playerParamIds.join(","),
              oponnent: needOpponentMarkers,
            },
          });

          statsResponses.push(playerStatsResponse);
        }

        if (selectedParameterIds.teamParamIds.length > 0 && teamId) {
          const teamStatsResponse = await $apiV1.get(`/stats/team/${teamId}`, {
            params: {
              matches: matchesList.join(","),
              params: selectedParameterIds.teamParamIds.join(","),
              oponnent: needOpponentMarkers,
            },
          });

          statsResponses.push(teamStatsResponse);
        }

        const stats = statsResponses.flatMap(
          (response) => response?.data?.stats || []
        );

        const allFetchedMarkers = stats.flatMap((stat) =>
          stat.parameters.flatMap((param) => param.markers)
        );

        const parameterColorMap = buildParameterColorMap(checkedAction);
        const markerColorMap = buildMarkerColorMap(
          stats,
          parameterColorMap
        );

        if (needOpponentMarkers) {
          setOpponentMarkerColorMap(markerColorMap);
        } else {
          setOwnMarkerColorMap(markerColorMap);
        }

        if (allFetchedMarkers.length === 0) {
          clearSideResults(needOpponentMarkers);
        }

        setMarkersFunc?.(allFetchedMarkers);
      } catch (error) {
        onErrorRef.current?.(error);
        console.error("Error fetching player stats:", error);
      } finally {
        updateLoading(-1);
      }
    },
    [
      checkedAction,
      clearSideResults,
      id,
      matchesList,
      selectedParameterIds,
      teamId,
      updateLoading,
    ]
  );

  const searchEventsForSide = useCallback(
    async ({ markersToSearch, side }) => {
      updateLoading(1);

      try {
        const basePayload =
          buildSearchPayload?.({
            markers: markersToSearch,
            side,
          }) || {};

        const payload = {
          ...basePayload,
          markers: basePayload.markers || markersToSearch,
          to_merge: false,
        };

        // Fast guard: fetch only the count first and skip the heavy markers_search when the
        // selection resolves to more events than we allow to render at once.
        const resultCount = await fetchEventsResultCount(payload);
        if (resultCount > EVENTS_RESULT_LIMIT) {
          setLimitExceededBySide((prev) => ({ ...prev, [side]: resultCount }));
          clearSideResults(side === "opponent");
          setAllMergeMarkers([]);
          return;
        }

        setLimitExceededBySide((prev) => ({ ...prev, [side]: null }));

        const result = await $apiV1.post("/stats/markers_search", payload);
        const resultData = selectedParameterIds.hasTeamSetPieceSelection
          ? filterSearchResultForPlayer({
              data: result.data,
              playerId,
              playerName,
            })
          : result.data;

        if (side === "opponent") {
          setResponseOpponents(resultData);
          setAllOpponentsMarkers(resultData.allMarkers || []);
        } else {
          setResponse(resultData);
          setAllOwnMarkers(resultData.allMarkers || []);
        }

        setAllMergeMarkers(resultData.allMarkers || []);

        const currentMax = findMaxSecondMarker(resultData.events || []);
        const maxForTimeline = keepMaxTimeline
          ? Math.max(timelineMaxRef.current, currentMax || 0)
          : currentMax;

        timelineMaxRef.current = maxForTimeline;

        setTimeLineRange({
          min: 0,
          max: Math.ceil(+maxForTimeline / 60 / 5) * 5,
        });
      } catch (error) {
        onErrorRef.current?.(error);
        console.error("Error searching player events:", error);
      } finally {
        updateLoading(-1);
      }
    },
    [
      buildSearchPayload,
      clearSideResults,
      keepMaxTimeline,
      playerId,
      playerName,
      selectedParameterIds,
      updateLoading,
    ]
  );

  useEffect(() => {
    const hasSelectedParams =
      selectedParameterIds.playerParamIds.length > 0 ||
      selectedParameterIds.teamParamIds.length > 0;

    if (matchesList.length > 0 && hasSelectedParams) {
      fetchPlayerStatsSide({
        needOpponentMarkers: false,
        setMarkersFunc: setMarkers,
      });
      fetchPlayerStatsSide({
        needOpponentMarkers: true,
        setMarkersFunc: setOpponentsMarkers,
      });
      return;
    }

    resetData();
  }, [
    matchesList,
    selectedParameterIds,
    checkedAction,
    fetchPlayerStatsSide,
    resetData,
  ]);

  useEffect(() => {
    if (markers.length > 0) {
      searchEventsForSide({ markersToSearch: markers, side: "own" });
    }
  }, [markers, mergeOffset, searchEventsForSide, ...searchDeps]);

  useEffect(() => {
    if (opponentsMarkers.length > 0) {
      searchEventsForSide({
        markersToSearch: opponentsMarkers,
        side: "opponent",
      });
    }
  }, [opponentsMarkers, mergeOffset, searchEventsForSide, ...searchDeps]);

  return {
    response,
    responseOpponents,
    ownMarkerColorMap,
    opponentMarkerColorMap,
    allOwnMarkers,
    allOpponentsMarkers,
    allMergeMarkers,
    loadingMarkers,
    limitExceededBySide,
    timeLineRange,
    resetData,
  };
};

export default usePlayerDualSideEventsData;
